/**
 * The fields the environment worked in adds to its nodes (the coding environment's language
 * and code), and the choices offered for each: its plugin's options, the ones this person added
 * (kept in the browser) and the values the graph already uses.
 */
import { createMemo, createSignal, type Accessor } from "solid-js";
import type { FieldDef } from "@protocol/FieldDef";
import type { Graph } from "@protocol/Graph";
import { choicesOf } from "../shared/fields";
import { load, save } from "../shared/storage";
import type { Environment } from "./environments";

/** Where the added choices are kept in the browser, by field id (bump the version if their shape changes). */
const KEY = "developer-app-choices-v1";

/** Where the languages added before fields came from environments were kept: they become the `language` field's. */
const OLD_LANGUAGES = "developer-app-languages-v1";

/** The fields of the environment worked in, and their choices. */
export interface Fields {
  /** The fields its nodes carry, in its plugin's order (none without an environment). */
  list: Accessor<FieldDef[]>;
  /** The choices offered for `field`: options, the ones added, and those the graph uses. */
  choices(field: FieldDef): string[];
  /** Adds a choice to field `id` (a case-insensitive duplicate is not added again). Returns the spelling kept. */
  add(id: string, value: string): string;
}

/** What the fields service is built from. */
export interface FieldsDeps {
  /** The graph shown (its nodes' values are offered too). */
  graph: () => Graph;
  /** The environment worked in. */
  environment: () => Environment | null;
}

/** The choices this person added, by field id, with the languages of before moved in. */
function loadAdded(): Record<string, string[]> {
  const added = load<Record<string, string[]>>(KEY, {});
  const languages = load<string[]>(OLD_LANGUAGES, []);
  if (languages.length && !added.language) added.language = languages;
  return added;
}

/** Creates the fields service. */
export function createFields({ graph, environment }: FieldsDeps): Fields {
  const [added, setAdded] = createSignal<Record<string, string[]>>(loadAdded());
  const list = createMemo(() => environment()?.fields ?? []);
  /** The choices of a field, worked out once per change of the graph or of what was added. */
  const choices = (field: FieldDef) => choicesOf(field, graph().nodes, added()[field.id] ?? []);
  return {
    list,
    choices,
    add(id, value) {
      const clean = value.trim().replace(/\s+/g, " ");
      const field = list().find((f) => f.id === id);
      const existing = (field ? choices(field) : added()[id] ?? []).find((c) => c.toLowerCase() === clean.toLowerCase());
      if (existing) return existing;
      const next = { ...added(), [id]: [...(added()[id] ?? []), clean] };
      setAdded(next);
      save(KEY, next);
      return clean;
    },
  };
}
